import { type Registry as Services, service } from '@ember/service';
import Model, { attr, type HasMany, hasMany } from '@ember-data/model';
import type { IconFlagCode } from '@mylighthouse/prism-ember/types/components/prism/icon-flag';
import { Type } from '@warp-drive/core-types/symbols';
import dayjs, { type Dayjs } from 'dayjs';
import type { DayjsTransformReturn } from 'frontend-utils/transforms/dayjs';
import {
  OTA_IDS,
  type OtaId,
} from 'frontend-utils/utils/constants/constant-values';
import { pushAndReturnPayload } from 'frontend-utils/utils/ember-data';
import type AiReply from 'lumen/models/reviews/ai-reply';
import type AjaxService from 'lumen/services/ajax';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';
import {
  isValidReviewScore,
  SCORE_FALLBACK_TITLE_MAP,
} from 'lumen/utils/constants/review-manager-constants';

export type ReplyOverrides = Partial<
  Pick<ReviewPayload['review'], 'ota_review_reply' | 'llm_generation_id'>
>;

interface ReviewPayload {
  review: {
    content: string;
    created_timestamp: string;
    external_id: string;
    id: string;
    language_code: string;
    lh_review_reply: string;
    llm_generation_id: number | null;
    negative: string;
    ota_id: string;
    ota_review_reply: string;
    positive: string;
    property_id: string;
    reply_sent_timestamp: string;
    reservation_id: string;
    score: number;
    title: string;
    user: string;
    user_country_code: string;
  };
}

export default class Review extends Model {
  [Type] = 'reviews/review' as const;

  @hasMany('reviews/ai-reply', { async: false, inverse: 'reviewId' })
  declare aiReplies: HasMany<AiReply>;
  @attr('string') declare content: string;
  @attr('string')
  declare createdTimestamp: string;
  @attr('string') declare externalId: string;
  @attr('string') declare languageCode: string;
  @attr('string') declare lhReviewReply: string;
  @attr('number') declare llmGenerationId: number | null;

  @attr('string') declare negative: string;
  @attr('string') declare otaId: OtaId;
  @attr('string') declare otaReviewReply: string;
  @attr('string') declare positive: string;
  @attr('string') declare processedOtaReviewReply: string; // OTA reply in review language
  @attr('string') declare propertyId?: string;
  @attr('dayjs') declare replySentTimestamp?: DayjsTransformReturn;
  @attr('string') declare reservationId: string;
  // Single number. Always score between 1 and 10.
  @attr('number') declare score: number;
  @attr('string') declare title: string;
  @attr('string') declare user: string;
  @attr('string') declare userCountryCode: IconFlagCode;

  @service declare ajax: AjaxService;

  @service('hotel-and-market')
  declare hotelAndMarket: Services['hotel-and-market'];
  @service declare intl: Services['intl'];

  get displayTitle(): string {
    if (this.title?.trim()) {
      return this.title;
    }

    if (isValidReviewScore(this.score)) {
      return this.intl.t(SCORE_FALLBACK_TITLE_MAP[this.score]);
    }
    return '';
  }

  get formattedDateFromNow(): string {
    const today = this.hotelAndMarket.getLocalTime();
    const daysAgo = today.diff(this.localizedCreatedTimestamp, 'day');

    if (daysAgo <= 3) {
      if (daysAgo === 0) {
        return this.intl.t('general.today');
      }

      return this.intl.t('review_manager.days_ago', { count: daysAgo });
    }

    return this.localizedCreatedTimestamp.format('L');
  }

  get formattedReplySentDate(): string {
    return this.localizedReplySentTimestamp?.format('L') ?? '';
  }

  get formattedReplySentTime(): string {
    return this.localizedReplySentTimestamp?.format('HH:mm') ?? '';
  }

  get formattedScore(): string {
    return `${this.score}/10`;
  }

  get hasReply(): boolean {
    return !!this.otaReviewReply && this.otaReviewReply.trim() !== '';
  }

  get isBookingdotcomScoreOnly(): boolean {
    return (
      this.otaId === OTA_IDS.BOOKINGDOTCOM &&
      !this.positive?.trim() &&
      !this.negative?.trim() &&
      !this.content?.trim()
    );
  }

  get isPendingApproval(): boolean {
    const hasLhReply = !!this.lhReviewReply && this.lhReviewReply.trim() !== '';
    return hasLhReply && !this.hasReply;
  }

  get lastAiReply(): AiReply | undefined {
    return this.aiReplies.reduce<AiReply | undefined>((latest, current) => {
      if (!current.aiReplyTimestamp) {
        return latest;
      }
      if (!latest?.aiReplyTimestamp) {
        return current;
      }

      return current.aiReplyTimestamp.isAfter(latest.aiReplyTimestamp)
        ? current
        : latest;
    }, undefined);
  }

  get localizedCreatedTimestamp(): Dayjs {
    return dayjs
      .utc(this.createdTimestamp)
      .utcOffset(this.hotelAndMarket.timezone * 60);
  }

  get localizedReplySentTimestamp(): Dayjs | null {
    if (!this.replySentTimestamp) {
      return null;
    }
    // Convert UTC timestamp to hotel timezone using dayjs utcOffset
    return dayjs
      .utc(this.replySentTimestamp)
      .utcOffset(this.hotelAndMarket.timezone * 60);
  }

  // @ts-expect-error: Property 'save' in type 'Review' is not assignable to the same property in base type 'Model'.
  async save(
    subscriptionId: string,
    replyOverrides?: ReplyOverrides,
  ): Promise<Review[] | null> {
    // Serialize this model's data (snake-casing etc..)
    // @ts-expect-error: The 'this' context of type 'this' is not assignable to method's 'this' of type 'MinimalLegacyRecord'.
    const data = this.serialize({ includeId: true }) as Record<string, unknown>;
    const payload = replyOverrides ? { ...data, ...replyOverrides } : data;

    // We override the save method to be able to post to apigateway instead of api/v3
    const response: ReviewPayload = await this.ajax.request(
      `${this.ajax?.host}/${APIGATEWAY}/review-manager/reviews/reply?subscription_id=${subscriptionId}`,
      {
        data: JSON.stringify({
          review: payload,
        }),
        method: 'POST',
        dataType: 'json',
        contentType: 'application/json; charset=utf-8',
      },
    );

    super.rollbackAttributes();

    // Push the backend response into the store so the record reflects server-side updates
    const normalisedResponse = pushAndReturnPayload(
      this.store,
      { reviews: [response.review] },
      'reviews/review',
    );
    const apiResponse = normalisedResponse['reviews'] as Review[];

    return apiResponse;
  }
}
